/**
 * 共通APIクライアント
 * - NEXT_PUBLIC_API_URL をベースURLとして使用
 * - 認証系ヘッダ（Authorization / X-Organization-Id / X-User-Id / X-Project-Id）を自動付与
 * - 401エラー時に自動的にログインページへリダイレクト
 * - validation_errors レスポンスを validationErrors にマップ
 */

import { STORAGE_KEYS, getStoredString, readStoredValue } from '@/lib/utils/storage';
import type { ApiResult } from '@/types/common';

// 401エラー時のコールバック（AuthProviderから設定される）
let onUnauthorizedCallback: (() => void) | null = null;

export function setOnUnauthorizedCallback(callback: () => void) {
  onUnauthorizedCallback = callback;
}

export function clearOnUnauthorizedCallback() {
  onUnauthorizedCallback = null;
}

export interface ApiClientOptions extends RequestInit {
  /** 401時のログインリダイレクトをスキップ */
  skipAuthRedirect?: boolean;
  /** 認証系ヘッダの自動付与をスキップ */
  skipAuthHeaders?: boolean;
  /** X-Organization-Id を明示指定（省略時はlocalStorageから自動付与） */
  organizationId?: number | string;
  /** X-User-Id を明示指定（省略時はlocalStorageから自動付与） */
  userId?: number | string;
  /** X-Project-Id を明示指定（指定時のみ付与） */
  projectId?: number | string;
}

/** HTTPステータスを含むAPIレスポンス結果 */
export interface ApiFetchResult<T> extends ApiResult<T> {
  status: number;
  /** 409応答時の競合詳細（backend base_controller.go の CaseConflictError 形式） */
  conflict?: unknown;
}

/** 認証系ヘッダを構築（呼び出し側の指定 > localStorage の順で解決） */
function buildAuthHeaders(options: ApiClientOptions): Record<string, string> {
  if (options.skipAuthHeaders || typeof window === 'undefined') {
    return {};
  }

  const headers: Record<string, string> = {};

  const token = getStoredString(STORAGE_KEYS.TOKEN);
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const organizationId = options.organizationId ?? readStoredValue(STORAGE_KEYS.ORGANIZATION_ID);
  if (organizationId !== null && organizationId !== undefined) {
    headers['X-Organization-Id'] = String(organizationId);
  }

  const userId = options.userId ?? readStoredValue(STORAGE_KEYS.USER_ID);
  if (userId !== null && userId !== undefined) {
    headers['X-User-Id'] = String(userId);
  }

  if (options.projectId !== null && options.projectId !== undefined) {
    headers['X-Project-Id'] = String(options.projectId);
  }

  return headers;
}

/**
 * 認証対応fetchラッパー（完全URL指定）
 * 401レスポンスを検出してログインページへリダイレクト
 */
export async function apiFetch<T = unknown>(
  url: string,
  options: ApiClientOptions = {}
): Promise<ApiFetchResult<T>> {
  const {
    skipAuthRedirect = false,
    skipAuthHeaders: _skipAuthHeaders,
    organizationId: _organizationId,
    userId: _userId,
    projectId: _projectId,
    ...fetchOptions
  } = options;

  try {
    const response = await fetch(url, {
      credentials: 'include',
      ...fetchOptions,
      headers: {
        'Content-Type': 'application/json',
        ...buildAuthHeaders(options),
        ...fetchOptions.headers,
      },
    });

    // 401 Unauthorized - セッション期限切れ
    if (response.status === 401 && !skipAuthRedirect) {
      if (onUnauthorizedCallback) {
        onUnauthorizedCallback();
      } else {
        // フォールバック: 直接リダイレクト(Reactツリー外のためuseRouterが使えない)
        // eslint-disable-next-line @next/next/no-location-assign-relative-destination
        window.location.href = '/login';
      }
      return { error: 'セッションが期限切れです。再度ログインしてください。', status: 401 };
    }

    // その他のエラー
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        error: errorData.error || `エラーが発生しました (${response.status})`,
        validationErrors: errorData.validation_errors,
        conflict: errorData.conflict,
        status: response.status,
      };
    }

    // 成功
    // エンベロープ({data: ...})なら中身を取り出す。
    // data?.data ?? data だと {data: null} のとき全体(エンベロープ)に
    // フォールバックして非配列になるため、'data'キーの有無で判定する。
    const json = await response.json().catch(() => null);
    const isEnvelope = json && typeof json === 'object' && !Array.isArray(json) && 'data' in json;
    const payload = isEnvelope ? (json as { data: unknown }).data : json;
    // data以外の直下キー(例: ?since=のdeletedIds)は、呼び出し側が型キャストして読めるよう保持する
    const siblingKeys = isEnvelope ? { ...(json as Record<string, unknown>) } : {};
    if (isEnvelope) delete (siblingKeys as { data?: unknown }).data;
    return { ...siblingKeys, data: payload as T, status: response.status };
  } catch (err) {
    return {
      error: err instanceof Error ? err.message : '通信エラーが発生しました',
      status: 0,
    };
  }
}

/**
 * 共通APIクライアント（エンドポイント指定）
 * NEXT_PUBLIC_API_URL をベースURLとして付与する
 */
export async function apiClient<T = unknown>(
  endpoint: string,
  options: ApiClientOptions = {}
): Promise<ApiFetchResult<T>> {
  return apiFetch<T>(`${process.env.NEXT_PUBLIC_API_URL}${endpoint}`, options);
}

/**
 * GET リクエスト
 */
export async function apiGet<T = unknown>(
  endpoint: string,
  options: ApiClientOptions = {}
): Promise<ApiFetchResult<T>> {
  return apiClient<T>(endpoint, { ...options, method: 'GET' });
}

/**
 * POST リクエスト
 */
export async function apiPost<T = unknown>(
  endpoint: string,
  body?: unknown,
  options: ApiClientOptions = {}
): Promise<ApiFetchResult<T>> {
  return apiClient<T>(endpoint, {
    ...options,
    method: 'POST',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

/**
 * PUT リクエスト
 */
export async function apiPut<T = unknown>(
  endpoint: string,
  body?: unknown,
  options: ApiClientOptions = {}
): Promise<ApiFetchResult<T>> {
  return apiClient<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

/**
 * PATCH リクエスト
 */
export async function apiPatch<T = unknown>(
  endpoint: string,
  body?: unknown,
  options: ApiClientOptions = {}
): Promise<ApiFetchResult<T>> {
  return apiClient<T>(endpoint, {
    ...options,
    method: 'PATCH',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

/**
 * DELETE リクエスト
 */
export async function apiDelete<T = unknown>(
  endpoint: string,
  options: ApiClientOptions = {}
): Promise<ApiFetchResult<T>> {
  return apiClient<T>(endpoint, { ...options, method: 'DELETE' });
}
